import React, { useMemo } from 'react'

import { ReactComponent as FavoriteSvg } from '../../assets/favorite.svg'
import { ReactComponent as NotFavoriteSvg } from '../../assets/notFavorite.svg'
import { ReactComponent as ZoomSvg } from '../../assets/buttonZoomSvg.svg'
import productUnavailable from '../../assets/product_unavailable.png'

import useWishList from '../../hooks/WishListContext'

import SendGTAGEvent from '../../utils/sendGTAGEvent'
import parseCurrency from '../../utils/parseCurrency';

import { IZoomModal } from '../../page/Products';

import * as S from './styles'

interface IProductCardItemProps {
    items: {
        images: {
            imageUrl: string;
        }[]

        Tamanho?: string;
        sellers: {
            sellerName: string;
            commertialOffer: {
                Price: number
                ListPrice: number;
                AvailableQuantity: number;
            };
        }[]
    }[];
    linkText: string;
    productName: string;
    productReference: string;
    brandBaseUrl: string | any;
    shouldUseNewTag: boolean;
    shouldShowButton?: boolean;
    textColor?: string;
    priceColor?: string;
    onGetInformationZoomModal?: (data: IZoomModal) => void;
    onOpenZoomModal?: () => void;
    sellerCode: string;
    wishlistId?: string;
    currentBrand: string;
}

const ProductCardItem: React.FC<IProductCardItemProps> = ({
    items,
    productName,
    productReference,
    linkText,
    brandBaseUrl,
    shouldUseNewTag,
    shouldShowButton,
    textColor,
    priceColor,
    onGetInformationZoomModal,
    onOpenZoomModal,
    sellerCode,
    wishlistId,
    currentBrand
}) => {

    const {
        wishListIsOpen,
        openedWishList,
        onAddProductHandler,
        onDeleteProductHandler,
    } = useWishList()

    const realItemMemo = useMemo(() => (
        items.find((item) => item.sellers.find(seller => seller.commertialOffer.Price > 0))
    ), [items])

    const sizesAvailableMemo = useMemo(() => (
        items.filter((item) => item.sellers.some((seller) => seller.commertialOffer.AvailableQuantity >= 0))
    ), [items])

    const Sizes = sizesAvailableMemo.map((item) => {
        const size = item.Tamanho;
        const availableQuantity = item.sellers[0].commertialOffer.AvailableQuantity;
        const styles = availableQuantity === 0 ? false : true;

        return { size, styles };
    });


    const gaUtmsMemo = useMemo(() => {
        const utms: { [key: string]: string } = {
            utm_campaign: sellerCode,
            utm_source: 'maislojinha',
            utm_medium: wishlistId || 'linkunico',
            utm_content: currentBrand,
        }
        const objectKeysSize = Object.keys(utms).length

        let newUtmText = ''
        let index = 0

        for (const key in utms) {
            index++
            if (!utms[key]) continue
            newUtmText = `${newUtmText}${key}=${utms[key]}${index !== objectKeysSize ? '&' : ''}`
        }

        return newUtmText
    }, [sellerCode, currentBrand, wishlistId])

    const linkToEcommerce = useMemo(() => `${brandBaseUrl}/${linkText}/p?${gaUtmsMemo}`, [brandBaseUrl, gaUtmsMemo, linkText])

    const treatedUrlImageMemo = useMemo(() => {
        if (items[0].images[0].imageUrl) {
            const originalUrl = items[0].images[0].imageUrl;
            return originalUrl.replace(/ids\/(\d+)\//, (match, id) => `ids/${id}-600-auto/`);
        }
        else {
            return productUnavailable
        }
    }, [items])

    const isFaroviteMemo = useMemo(() => {
        if (wishListIsOpen && openedWishList.products.length > 0) {
            return openedWishList.products.find((product) => product.productRef === linkText)
        }
    }, [openedWishList.products, linkText, wishListIsOpen])

    const checkIfHasDiscount = useMemo(() => {
        const item = realItemMemo?.sellers.find(seller => seller.commertialOffer.Price > 0)

        return item?.commertialOffer.Price !== item?.commertialOffer.ListPrice
    }, [realItemMemo?.sellers])

    const priceJsx = useMemo(() => {
        return checkIfHasDiscount ?
            (
                <S.PricesContainer priceColor={priceColor}>
                    <p className='p__old__value'> {parseCurrency(realItemMemo?.sellers[0].commertialOffer.ListPrice || 0)} </p>
                    <p className='p__discount'> {parseCurrency(realItemMemo?.sellers[0].commertialOffer.Price || 0)} </p>
                </S.PricesContainer>
            )
            : <p> {parseCurrency(realItemMemo?.sellers[0].commertialOffer.Price || 0)} </p>
    }, [checkIfHasDiscount, priceColor, realItemMemo?.sellers])

    const ContainerJSX = useMemo(() => {
        const containerWishlistOpen: React.FC = ({ children }) => (
            <S.Container
                onClick={wishListIsOpen && !isFaroviteMemo ?
                    () => {
                        onAddProductHandler(
                            linkText,
                            productName,
                            items[0].images[0].imageUrl,
                            productReference
                        )
                        SendGTAGEvent(
                            'add_to_wishlist',
                            'Lojinha',
                            'Adiciona item na lojinha',
                            productName)
                    }
                    :

                    () => {
                        onDeleteProductHandler(linkText)
                        SendGTAGEvent(
                            'remove_to_wishlist',
                            'Lojinha',
                            'Remove item da lojinha',
                            productName)

                    }}> {children} </S.Container>
        )

        const containerOpenZoomModal: React.FC = ({ children }) => (
            <S.Container onClick={
                onGetInformationZoomModal && onOpenZoomModal
                    ? () => {
                        onGetInformationZoomModal({
                            img: treatedUrlImageMemo,
                            name: productName,
                            ref: productReference,
                            href: linkToEcommerce,
                            price: {
                                discountPrice: realItemMemo?.sellers.find(seller => seller.commertialOffer.Price)?.commertialOffer.Price || 0,
                                oldPrice: realItemMemo?.sellers.find(seller => seller.commertialOffer.ListPrice)?.commertialOffer.ListPrice || 0,
                            }
                        })
                        onOpenZoomModal()
                        SendGTAGEvent(
                            'click_imagem_produto',
                            'Produto',
                            'Abre modal de produto único',
                            productName)
                    }
                    : () => null}>
                {children}
            </S.Container>
        )
        const containerOpenLinkEcommerce: React.FC = ({ children }) => (
            <S.Container>
                <a href={linkToEcommerce} target="_blank" rel="noreferrer"> {children} </a>
            </S.Container>
        )

        return wishListIsOpen
            ? containerWishlistOpen
            : onOpenZoomModal
                ? containerOpenZoomModal
                : containerOpenLinkEcommerce

    }, [isFaroviteMemo, items, linkText, linkToEcommerce, onAddProductHandler, onDeleteProductHandler, onGetInformationZoomModal, onOpenZoomModal, productName, productReference, realItemMemo?.sellers, treatedUrlImageMemo, wishListIsOpen])

    return (
        <ContainerJSX>
            <S.ImageCardContainer>
                {
                    shouldUseNewTag &&
                    <S.NewImageContainer>
                        <div></div>
                        {!wishListIsOpen && <div className='div_svg'><ZoomSvg className='svg__zoom' /></div>}
                    </S.NewImageContainer>
                }

                {
                    wishListIsOpen ? isFaroviteMemo
                        ? <FavoriteSvg className="svg__favorite" />
                        : <NotFavoriteSvg className="svg__favorite" />
                        : null
                }
                <img src={treatedUrlImageMemo} alt={productName} loading="lazy" />
            </S.ImageCardContainer>
            <S.InfoCardContainer color={textColor} priceColor={priceColor} >
                <label> {productName} </label>
                {priceJsx}
                {
                    shouldShowButton ?
                        <span />
                        : <small> Ref.: {productReference} </small>
                }
                <div>
                    {!shouldShowButton && Sizes.map(({ size, styles }) => (<S.ContentSizes isAvaliable={styles} key={size}><h6>{size}</h6></S.ContentSizes>))}
                </div>
            </S.InfoCardContainer>
        </ContainerJSX>
    )
}

export default ProductCardItem